import { useState, useEffect, useCallback, useRef } from "react";
import { collection, query, orderBy, limit, getDocs, doc, updateDoc, startAfter, where } from "firebase/firestore";
import { db } from "../../../firebase";
import { F, D } from "../../../styles/palette";
import { DC, Bdg } from "../../shared";
import { logAudit } from "../../../utils/auditTrail";

const PAGE_SIZE = 20;

export default function AdminUsers({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const lastDoc = useRef(null);
  const debounce = useRef(null);

  const loadUsers = useCallback(async (append = false) => {
    setLoading(true);
    try {
      let q = query(collection(db, "users"), orderBy("name"), limit(PAGE_SIZE));
      if (append && lastDoc.current) {
        q = query(collection(db, "users"), orderBy("name"), startAfter(lastDoc.current), limit(PAGE_SIZE));
      }
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      lastDoc.current = snap.docs[snap.docs.length - 1] || null;
      setHasMore(snap.docs.length === PAGE_SIZE);
      setUsers(prev => append ? [...prev, ...docs] : docs);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const searchUsers = useCallback(async (term) => {
    if (!term.trim()) { lastDoc.current = null; return loadUsers(); }
    setLoading(true);
    try {
      // Search by email (exact match) or name prefix
      const t = term.trim();
      let docs = [];
      if (t.includes("@")) {
        const snap = await getDocs(query(collection(db, "users"), where("email", "==", t)));
        docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } else {
        const upper = t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
        const end = upper.slice(0, -1) + String.fromCharCode(upper.charCodeAt(upper.length - 1) + 1);
        const snap = await getDocs(query(collection(db, "users"), where("name", ">=", upper), where("name", "<", end), orderBy("name"), limit(PAGE_SIZE)));
        docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      setUsers(docs);
      setHasMore(false);
      lastDoc.current = null;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [loadUsers]);

  const handleSearch = (val) => {
    setSearchTerm(val);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => searchUsers(val), 400);
  };

  const suspendUser = async (uid, email) => {
    setConfirming(null);
    await updateDoc(doc(db, "users", uid), { suspended: true });
    await logAudit("suspender_usuario", "user", uid, user.uid, user.email, `Suspendido: ${email}`);
    setUsers(prev => prev.map(u => u.id === uid ? { ...u, suspended: true } : u));
  };

  const reactivateUser = async (uid, email) => {
    await updateDoc(doc(db, "users", uid), { suspended: false });
    await logAudit("reactivar_usuario", "user", uid, user.uid, user.email, `Reactivado: ${email}`);
    setUsers(prev => prev.map(u => u.id === uid ? { ...u, suspended: false } : u));
  };

  const roleColor = (role) => role === "admin" ? "#dc2626" : role === "doctor" ? "#0284c7" : "#16a34a";

  return (
    <div>
      {/* Search */}
      <div style={{ position: "relative", marginBottom: 14 }}>
        <input type="text" value={searchTerm} onChange={e => handleSearch(e.target.value)}
          placeholder="Buscar por nombre o email..."
          style={{ width: "100%", padding: "10px 14px 10px 36px", borderRadius: 8, border: `1px solid ${D.bdr}`, background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 13, fontFamily: F.b, boxSizing: "border-box" }} />
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 15, opacity: 0.4 }}>&#128269;</span>
      </div>

      {/* Confirm dialog */}
      {confirming && (
        <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 10, padding: "14px 18px", marginBottom: 12 }}>
          <p style={{ color: "#fca5a5", fontSize: 13, margin: "0 0 10px" }}>
            ¿Suspender la cuenta de {confirming.email}? No podrá iniciar sesión.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => suspendUser(confirming.id, confirming.email)}
              style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#dc2626", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Confirmar</button>
            <button onClick={() => setConfirming(null)}
              style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${D.bdr}`, background: "transparent", color: "#94a3b8", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
          </div>
        </div>
      )}

      {loading && users.length === 0 && <p style={{ color: D.txM, fontSize: 13, textAlign: "center", padding: 20 }}>Cargando usuarios...</p>}

      {!loading && users.length === 0 && <DC><p style={{ color: D.txM, fontSize: 13, textAlign: "center" }}>No se encontraron usuarios</p></DC>}

      {users.map(u => {
        const isExp = expanded === u.id;
        return (
          <div key={u.id} onClick={() => setExpanded(isExp ? null : u.id)}
            style={{ background: D.card, border: `1px solid ${u.suspended ? "rgba(220,38,38,0.3)" : D.bdr}`, borderRadius: 12, padding: "12px 16px", marginBottom: 6, cursor: "pointer", backdropFilter: "blur(12px)", opacity: u.suspended ? 0.6 : 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600 }}>{u.name || "Sin nombre"}</span>
                <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>{u.email}</span>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <Bdg t={u.role || "patient"} c={roleColor(u.role)} />
                {u.suspended && <Bdg t="Suspendido" c="#dc2626" />}
                {u.totpEnabled && <Bdg t="2FA" c="#16a34a" />}
              </div>
            </div>

            {isExp && (
              <div style={{ marginTop: 10, borderTop: `1px solid ${D.bdr}`, paddingTop: 10 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: "#64748b" }}>UID: <span style={{ color: "#94a3b8", fontFamily: F.m }}>{u.id.slice(0, 12)}...</span></div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>Registro: <span style={{ color: "#94a3b8" }}>{u.createdAt?.toDate?.()?.toLocaleDateString("es-CO") || "N/A"}</span></div>
                  {u.registroMedico && <div style={{ fontSize: 11, color: "#64748b" }}>RM: <span style={{ color: "#94a3b8" }}>{u.registroMedico}</span></div>}
                  <div style={{ fontSize: 11, color: "#64748b" }}>Dispositivos: <span style={{ color: "#94a3b8" }}>{u.devices?.length || 0}</span></div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {!u.suspended && u.id !== user.uid && (
                    <button onClick={() => setConfirming({ id: u.id, email: u.email })}
                      style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#f87171", fontSize: 11, cursor: "pointer", fontFamily: F.b }}>
                      Suspender
                    </button>
                  )}
                  {u.suspended && (
                    <button onClick={() => reactivateUser(u.id, u.email)}
                      style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(22,163,74,0.3)", background: "transparent", color: "#4ade80", fontSize: 11, cursor: "pointer", fontFamily: F.b }}>
                      Reactivar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <button onClick={() => loadUsers(true)} disabled={loading}
            style={{ padding: "8px 24px", borderRadius: 8, border: `1px solid ${D.bdr}`, background: "transparent", color: D.ac, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: F.b }}>
            {loading ? "Cargando..." : "Cargar más"}
          </button>
        </div>
      )}
    </div>
  );
}
